/**
 * İstekler / Senden beklenenler (artboard F_Istekler). Inline actions are allowed here
 * because none of them acts externally: Kabul et opens a sheet (target date, note) and
 * creates Sercan's task, Cevapla records an answer, Ertele and Reddet ask for a choice.
 */

import { useEffect, useState } from 'react';
import type { DesignRequest, PhotoRequest, QuestionRequest, Snapshot, TwinRequest } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { AGENT_NAMES, AGENT_SHAPES, counts, openRequests, requestsWithStatus } from '../app/selectors.js';
import { AgentGlyph, Icon } from '../components/icons.js';
import { TierBadge, TypeChip } from '../components/primitives.js';
import { useSheets } from '../components/sheets.js';
import { AppBar, BottomTabBar, StatusArea } from '../components/shell.js';
import { SourceLine } from '../components/source.js';
import { formatAge, formatLongDate, formatRelativeClock } from '../lib/format.js';
import { Accent, EmptyState, Loading, Segmented, SummaryHeadline } from './common.js';

type RequestTab = 'acik' | 'kabul' | 'ertelenen';

function DesignBrief({ request }: { request: DesignRequest }) {
  return (
    <>
      <div className="brief">
        <span className="key ink-2 brief__key">Girdiler</span>
        <span className="brief__value">
          {request.inputs.map((input) => (
            <span key={input} className="brief__line">
              {input}
            </span>
          ))}
        </span>
      </div>
      <div className="criteria">
        <div className="key ink-2">Kabul kriteri</div>
        <dl className="criteria__grid">
          {request.acceptance.map((row) => (
            <div key={row.key} className="criteria__row">
              <dt className="key">{row.key}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
        <div className="criteria__folder">
          <Icon name="folder" size={14} className="ink" />
          Teslim
          <span className="mono criteria__path">{request.deliveryFolder}</span>
        </div>
      </div>
      <div className="facts facts--2">
        <div className="facts__cell">
          <div className="key ink-2">{request.due.key}</div>
          <div className="facts__value">
            {request.due.value} {request.due.note ? <span className="facts__note">· {request.due.note}</span> : null}
          </div>
        </div>
        <div className="facts__cell">
          <div className="key ink-2">Hedef fiyat</div>
          <div className="facts__value facts__value--row">
            <TierBadge tier="T4" topic="PARA" variant="mini-tier" />
            {request.priceNote}
          </div>
        </div>
      </div>
    </>
  );
}

function PhotoFacts({ request }: { request: PhotoRequest }) {
  return (
    <div className="facts facts--4">
      {request.facts.map((fact) => (
        <div key={fact.key} className="facts__cell">
          <div className="key ink-2 nowrap">{fact.key}</div>
          <div className="facts__value">{fact.value}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * A question explains itself (v0.1.42): the English original under a Turkish title, why the
 * agent asks, the customer's own words, the answer choices and what an answer triggers.
 */
function QuestionBrief({ request, onPick }: { request: QuestionRequest; onPick: (option: string) => void }) {
  const showEn = request.question.tr !== null && request.question.tr !== request.question.en;
  return (
    <div className="qbrief">
      {showEn ? (
        <p lang="en" className="qbrief__en">
          {request.question.en}
        </p>
      ) : null}
      {request.why ? (
        <p className="qbrief__row">
          <span className="key ink-2">Neden soruyor</span> {request.why}
        </p>
      ) : null}
      {request.customerQuote ? (
        <blockquote lang="en" className="qbrief__quote">
          {request.customerQuote}
        </blockquote>
      ) : null}
      {request.options.length > 0 ? (
        <fieldset className="qoptions">
          <legend className="sr-only">Hazır cevaplar</legend>
          {request.options.map((option) => (
            <button key={option} type="button" className="qoption" onClick={() => onPick(option)}>
              {option}
            </button>
          ))}
        </fieldset>
      ) : null}
      <p className="qbrief__row">
        <span className="key ink-2">Cevaplayınca</span> {request.afterAnswer}
      </p>
    </div>
  );
}

export function RequestCard({
  request,
  now,
  focused = false,
}: {
  request: TwinRequest;
  now: number;
  focused?: boolean;
}) {
  const sheets = useSheets();
  const store = useStore();
  const titleId = `ist-${request.id}`;
  const age = formatAge(now - request.createdAt);
  const open = request.status === 'open';
  const primaryLabel = request.type === 'question' ? 'Cevapla' : 'Kabul et';
  const primarySub = request.type === 'question' ? 'taslak onayına gelir' : 'tarih seç';
  return (
    <article
      id={`istek-${request.id}`}
      aria-labelledby={titleId}
      className={focused ? 'rcard rcard--focused' : 'rcard'}
      tabIndex={focused ? -1 : undefined}
    >
      <div className="rcard__head">
        <TypeChip type={request.type} />
        <span
          className={
            request.customerWaiting && now - request.createdAt > 12 * 3_600_000 ? 'rcard__age warning' : 'rcard__age'
          }
        >
          {request.customerWaiting ? 'müşteri bekliyor · ' : null}
          <span className="mono">{age}</span>
        </span>
      </div>
      <h2 id={titleId} className="rcard__title">
        {request.title}
      </h2>
      <div className="rcard__from">
        <AgentGlyph shape={AGENT_SHAPES[request.agent]} />
        {AGENT_NAMES[request.agent]} · <span className="mono">{formatRelativeClock(request.createdAt, now)}</span>
      </div>
      <SourceLine source={request.source} now={now} />
      {request.type === 'question' && open ? null : <p className="rcard__context">{request.context}</p>}
      {request.type === 'question' && open ? (
        <QuestionBrief
          request={request}
          onPick={(option) => sheets.open({ kind: 'answer', requestId: request.id, prefill: option })}
        />
      ) : null}
      {request.type === 'design' && open ? <DesignBrief request={request} /> : null}
      {request.type === 'photo' && open ? <PhotoFacts request={request} /> : null}
      {open ? (
        <div className="rcard__actions">
          <button
            type="button"
            className="rcard__primary"
            aria-describedby={titleId}
            onClick={() =>
              sheets.open(
                request.type === 'question'
                  ? { kind: 'answer', requestId: request.id }
                  : { kind: 'accept', requestId: request.id },
              )
            }
          >
            <span className="rcard__primary-label">{primaryLabel}</span>
            <span className="rcard__primary-sub">{primarySub}</span>
          </button>
          <button
            type="button"
            className="btn btn--secondary rcard__secondary"
            aria-describedby={titleId}
            onClick={() => sheets.open({ kind: 'defer', requestId: request.id })}
          >
            <Icon name="clock" size={16} />
            Ertele
          </button>
          <button
            type="button"
            className="btn btn--secondary rcard__secondary"
            aria-describedby={titleId}
            onClick={() => sheets.open({ kind: 'request-reject', requestId: request.id })}
          >
            <Icon name="x" size={16} />
            Reddet
          </button>
        </div>
      ) : (
        <div className="rcard__status">
          <span>{request.statusNote}</span>
          {request.status === 'accepted' ? (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => store.showToast('Dosya yükleme sunucu bağlanınca açılır', { icon: 'info' })}
            >
              Teslim et
            </button>
          ) : null}
        </div>
      )}
    </article>
  );
}

export function requestSubline(snapshot: Snapshot): string {
  const c = counts(snapshot);
  const parts: string[] = [];
  if (c.questions > 0) parts.push(`${c.questions} soru`);
  if (c.designs > 0) parts.push(`${c.designs} tasarım`);
  if (c.photos > 0) parts.push(`${c.photos} görsel`);
  const slots = snapshot.requestSlots;
  parts.push(`iş sınırı ${slots.used}/${slots.max}${slots.used >= slots.max ? ' · dolu' : ''}`);
  return parts.join(' · ');
}

export function useRequestTabs(snapshot: Snapshot | null) {
  const [tab, setTab] = useState<RequestTab>('acik');
  const lists = snapshot
    ? {
        acik: openRequests(snapshot),
        kabul: requestsWithStatus(snapshot, 'accepted'),
        ertelenen: requestsWithStatus(snapshot, 'deferred'),
      }
    : { acik: [], kabul: [], ertelenen: [] };
  const options = [
    { id: 'acik' as const, label: 'Açık', count: lists.acik.length },
    { id: 'kabul' as const, label: 'Kabul edilen', count: lists.kabul.length },
    { id: 'ertelenen' as const, label: 'Ertelenen', count: lists.ertelenen.length },
  ];
  return { tab, setTab, lists, options };
}

export function useFocusRequest(focus: string | null, ready: boolean) {
  useEffect(() => {
    if (!focus || !ready) return;
    const element = document.getElementById(`istek-${focus}`);
    element?.scrollIntoView?.({ block: 'start' });
    element?.focus?.({ preventScroll: true });
  }, [focus, ready]);
}

export function Istekler({ focus }: { focus: string | null }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const { tab, setTab, lists, options } = useRequestTabs(snapshot);
  useFocusRequest(focus, snapshot !== null);
  const subtitle = `İstekler · ${formatLongDate(state.now)}`;
  if (!snapshot) {
    return (
      <>
        <AppBar title="Senden beklenenler" subtitle={subtitle} />
        <Loading />
        <BottomTabBar active="istekler" />
      </>
    );
  }
  const c = counts(snapshot);
  const list = lists[tab];
  return (
    <>
      <AppBar title="Senden beklenenler" subtitle={subtitle} />
      <StatusArea />
      <SummaryHeadline
        title={
          c.requests === 0 ? (
            'Ekip senden bir şey beklemiyor'
          ) : (
            <>
              <Accent>{c.requests}</Accent> istek senden bekleniyor
            </>
          )
        }
        subline={requestSubline(snapshot)}
      />
      <Segmented label="İstek durumu" controls="ist-panel" value={tab} onChange={setTab} options={options} />
      <main
        className="phone-main phone-main--cards"
        id="ist-panel"
        role="tabpanel"
        aria-labelledby={`tab-ist-panel-${tab}`}
      >
        {list.length === 0 ? (
          <EmptyState
            title="Ekip senden bir şey beklemiyor"
            text="Bir soru ya da tasarım isteği gelince burada görünür."
          />
        ) : (
          <div className="rcards">
            {list.map((request) => (
              <RequestCard key={request.id} request={request} now={state.now} focused={focus === request.id} />
            ))}
          </div>
        )}
      </main>
      <BottomTabBar active="istekler" />
    </>
  );
}
